'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var useLanguage = require('../../hooks/useLanguage.js');

const useReturnReasonsPath = _ref => {
  let {
    orderItem,
    selectedItem,
    value,
    returnEnabled,
    returnQuantityMustBeAll = false,
    exchangeEnabled,
    skipExchangeSelection,
    skipUpsellSelection,
    stepsProps,
    defaultType
  } = _ref;
  const backLabel = useLanguage["default"]({
    langKey: 'returnsActionsBack'
  }).value ?? '';
  const nextLabel = useLanguage["default"]({
    langKey: 'returnsActionsNext'
  }).value ?? '';
  const submitLabel = nextLabel;
  const noThanksLabel = useLanguage["default"]({
    langKey: 'returnsReasonsExchangeUpsellNoThanks'
  }).value ?? '';

  // compute the steps / flow required in the return reasons form
  const steps = React.useMemo(() => {
    var _stepsProps$pictures, _value$selection, _value$selection$, _value$selection2, _value$selection2$, _value$selection2$$co;
    if (selectedItem) return ['editSelector'];
    const ret = [];
    const isExchangeFlow = (value === null || value === void 0 ? void 0 : value.type) === 'exchange' || (value === null || value === void 0 ? void 0 : value.type) === 'exchangeForCredit';
    if (!defaultType) ret.push('typeSelector');
    if (isExchangeFlow) {
      var _stepsProps$exchanges, _stepsProps$exchanges2;
      if (!skipExchangeSelection) ret.push('exchangeItemSelector');
      if (stepsProps !== null && stepsProps !== void 0 && (_stepsProps$exchanges = stepsProps.exchanges) !== null && _stepsProps$exchanges !== void 0 && _stepsProps$exchanges.simpleFlow && orderItem.remainingQuantity > 1) ret.push('quantitySelector');

      // simple exchange flow would not ask for reasons, comments, etc
      if (stepsProps !== null && stepsProps !== void 0 && (_stepsProps$exchanges2 = stepsProps.exchanges) !== null && _stepsProps$exchanges2 !== void 0 && _stepsProps$exchanges2.simpleFlow) return ret;
    }

    // remaining steps are return flow or exchange flow with details
    ret.push('reasonSelector');
    if (orderItem.remainingQuantity > 1) {
      if (returnQuantityMustBeAll && orderItem.remainingQuantity === orderItem.orderedQuantity) {
        ret.push('quantityAll');
      } else {
        ret.push('quantitySelector');
      }
    }
    if (stepsProps !== null && stepsProps !== void 0 && (_stepsProps$pictures = stepsProps.pictures) !== null && _stepsProps$pictures !== void 0 && _stepsProps$pictures.enabled) ret.push('picturesUploader');

    // If the comments config is undefined OR set as allowed, we add the commentsForm as a step.
    if (!(value !== null && value !== void 0 && (_value$selection = value.selection) !== null && _value$selection !== void 0 && (_value$selection$ = _value$selection[0]) !== null && _value$selection$ !== void 0 && _value$selection$.comments) || value !== null && value !== void 0 && (_value$selection2 = value.selection) !== null && _value$selection2 !== void 0 && (_value$selection2$ = _value$selection2[0]) !== null && _value$selection2$ !== void 0 && (_value$selection2$$co = _value$selection2$.comments) !== null && _value$selection2$$co !== void 0 && _value$selection2$$co.allowed) {
      ret.push('commentsForm');
    }
    if (exchangeEnabled && !isExchangeFlow && !skipUpsellSelection) ret.push('upsellSelector');
    return ret;
  }, [orderItem, selectedItem, defaultType, value, exchangeEnabled, returnQuantityMustBeAll, skipExchangeSelection, skipUpsellSelection, stepsProps]);
  const [history, setHistory] = React.useState([]);
  const [currentStepIndex, setCurrentStepIndex] = React.useState(0);
  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex >= steps.length - 1;
  const isCompleted = currentStepIndex >= steps.length;
  const path = React.useMemo(() => steps.filter((d, i) => i <= currentStepIndex).map(step => {
    const data = {
      step,
      options: {},
      navigation: []
    };
    const nextButton = {
      type: isLastStep ? 'submit' : 'next',
      label: isLastStep ? submitLabel : nextLabel,
      disabled: true
    };
    if (step !== 'editSelector') {
      data.navigation.push({
        type: 'back',
        label: backLabel,
        disabled: false,
        outline: true
      });
    }
    switch (step) {
      case 'typeSelector':
        {
          data.options.types = [];
          if (returnEnabled) data.options.types.push('return');
          if (exchangeEnabled) data.options.types.push('exchange');
          break;
        }
      case 'reasonSelector':
        {
          data.options.reasons = orderItem.availableReasons ?? [];
          break;
        }
      case 'quantityAll':
        {
          data.options.totalQuantity = orderItem.remainingQuantity;
          break;
        }
      case 'quantitySelector':
        {
          data.options.totalQuantity = orderItem.remainingQuantity;
          break;
        }
      case 'picturesUploader':
        {
          var _stepsProps$pictures2, _stepsProps$pictures3, _stepsProps$pictures4, _stepsProps$pictures5, _stepsProps$pictures6, _value$pictures;
          data.options.pictures = {
            minCount: stepsProps !== null && stepsProps !== void 0 && (_stepsProps$pictures2 = stepsProps.pictures) !== null && _stepsProps$pictures2 !== void 0 && _stepsProps$pictures2.enabled ? 1 : 0,
            maxCount: stepsProps === null || stepsProps === void 0 ? void 0 : (_stepsProps$pictures3 = stepsProps.pictures) === null || _stepsProps$pictures3 === void 0 ? void 0 : _stepsProps$pictures3.maxCount,
            maxSize: stepsProps === null || stepsProps === void 0 ? void 0 : (_stepsProps$pictures4 = stepsProps.pictures) === null || _stepsProps$pictures4 === void 0 ? void 0 : _stepsProps$pictures4.maxSize
          };
          const maxCount = (stepsProps === null || stepsProps === void 0 ? void 0 : (_stepsProps$pictures5 = stepsProps.pictures) === null || _stepsProps$pictures5 === void 0 ? void 0 : _stepsProps$pictures5.maxCount) ?? Number.MAX_VALUE;
          const minCount = stepsProps !== null && stepsProps !== void 0 && (_stepsProps$pictures6 = stepsProps.pictures) !== null && _stepsProps$pictures6 !== void 0 && _stepsProps$pictures6.required ? 1 : 0;
          const count = ((_value$pictures = value.pictures) === null || _value$pictures === void 0 ? void 0 : _value$pictures.length) ?? 0;
          data.navigation.push({
            ...nextButton,
            disabled: count < minCount || count > maxCount
          });
          break;
        }
      case 'commentsForm':
        {
          var _stepsProps$comments, _stepsProps$comments2;
          data.options.consumerCommentsRequired = stepsProps === null || stepsProps === void 0 ? void 0 : (_stepsProps$comments = stepsProps.comments) === null || _stepsProps$comments === void 0 ? void 0 : _stepsProps$comments.required;
          data.navigation.push({
            ...nextButton,
            disabled: !!(stepsProps !== null && stepsProps !== void 0 && (_stepsProps$comments2 = stepsProps.comments) !== null && _stepsProps$comments2 !== void 0 && _stepsProps$comments2.required) && !value.customerComments
          });
          break;
        }
      case 'exchangeItemSelector':
      case 'upsellSelector':
        {
          var _value$exchangeTo;
          const upsell = step === 'upsellSelector';
          const skipExchange = upsell && Object.entries(value.exchangeSelections ?? {}).length === 0;
          const notSelected = !value.exchangeTo || !((_value$exchangeTo = value.exchangeTo) !== null && _value$exchangeTo !== void 0 && _value$exchangeTo.available);
          data.navigation.push({
            type: isLastStep ? 'submit' : 'next',
            label: !isLastStep ? nextLabel : skipExchange ? noThanksLabel : submitLabel,
            disabled: !skipExchange && notSelected
          });
          break;
        }
    }
    return data;
  }), [orderItem, value, returnEnabled, exchangeEnabled, stepsProps, steps, currentStepIndex, isLastStep, backLabel, nextLabel, submitLabel, noThanksLabel]);
  const next = React.useCallback(payload => {
    let saveState = {
      ...payload
    };
    let nextStepIndex = currentStepIndex + 1;
    const _currentStep = ['quantityAll', 'quantitySelector'].includes(currentStep) ? 'quantity' : currentStep;
    switch (_currentStep) {
      case 'typeSelector':
        {
          // we don't need to remember the current type selection, so we won't
          // show a returning/exchanging tag in item preview
          saveState = {
            ...saveState,
            type: undefined
          };
          break;
        }
      case 'reasonSelector':
        {
          var _reason$childIds;
          const reason = saveState.path[saveState.path.length - 1];
          const hasChild = ((reason === null || reason === void 0 ? void 0 : (_reason$childIds = reason.childIds) === null || _reason$childIds === void 0 ? void 0 : _reason$childIds.length) ?? 0) > 0;
          if (reason && hasChild) {
            nextStepIndex = currentStepIndex;
          }

          // we don't need to remember the current reason selection, otherwise
          // when user go back, it brings user to the child reason page.
          const newPath = saveState.path.slice(0, -1);
          const parent = newPath[newPath.length - 1];
          const grandparent = newPath[newPath.length - 2];
          saveState = {
            ...saveState,
            path: newPath,
            selectedReason: {
              id: (parent === null || parent === void 0 ? void 0 : parent.id) ?? '',
              name: (parent === null || parent === void 0 ? void 0 : parent.name) ?? '',
              category: parent === null || parent === void 0 ? void 0 : parent.name,
              parent: grandparent === null || grandparent === void 0 ? void 0 : grandparent.id
            }
          };
          break;
        }
      case 'quantity':
        {
          saveState = {
            ...saveState,
            quantity: undefined
          };
          break;
        }
    }
    setHistory(h => [...h, {
      step: currentStep,
      stepIndex: currentStepIndex,
      payload: saveState
    }]);
    setCurrentStepIndex(nextStepIndex);
  }, [currentStep, currentStepIndex]);
  const back = React.useCallback(() => {
    const previousStep = history[history.length - 1];
    const updates = history.slice(0, -1);
    if (previousStep) {
      setHistory(updates);
      setCurrentStepIndex(previousStep === null || previousStep === void 0 ? void 0 : previousStep.stepIndex);
    }
    return previousStep;
  }, [history]);
  return React.useMemo(() => ({
    path,
    isLastStep,
    isCompleted,
    history,
    next,
    back
  }), [path, isLastStep, isCompleted, history, next, back]);
};

exports.useReturnReasonsPath = useReturnReasonsPath;
